import * as React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { CreditCard, Shield, ChevronDown, ChevronUp } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { InitiatedByBadge } from '@/components/payments/initiated-by-badge';
import { PaymentStatusBadge } from '@/components/payments/payment-status-badge';
import { getCryptoOnchainAssetLabel, getPaymentMethodDisplay } from '@/components/payments/payment-method-cell';
import { PaymentDetails } from '@/lib/api/payments';
import { formatMoney } from "@/lib/money";
import { DateDisplay } from '@/components/date-display';

const HIDDEN_METADATA_KEYS = new Set([
  'cryptoProvider',
  'cryptoShortUrl',
  'cryptoReceivedAmount',
]);

const APM_LABELS: Record<string, string> = {
  klarna: 'Klarna',
  afterpay: 'Afterpay',
  paypal: 'PayPal',
  ideal: 'iDEAL',
  bancontact: 'Bancontact',
  twint: 'TWINT',
  revolut_pay: 'Revolut Pay',
  skrill: 'Skrill',
};

function formatPaymentType(value: string): string {
  if (value === 'card') return 'Card';
  if (value === 'apple_pay') return 'Apple Pay';
  if (value === 'google_pay') return 'Google Pay';
  if (value === 'us_bank_account') return 'US Bank Account';
  if (value === 'sepa_debit') return 'SEPA Debit';
  const match = value.match(/^apm_(?:stripe|airwallex)_(.+)$/);
  if (match) return APM_LABELS[match[1]] ?? match[1].replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return value;
}

function getPaymentTypeDisplay(payment: PaymentDetails, t: TFunction<'payments'>): string {
  const type = payment.paymentInstrument?.type;
  if (type) return formatPaymentType(type);
  // On-chain crypto: the coin and the network, `ETH (Ethereum Sepolia)`.
  const display = getPaymentMethodDisplay(payment);
  if (display.kind === 'crypto') {
    return getCryptoOnchainAssetLabel(display) ?? t('table.filters.paymentMethod.crypto');
  }
  const meta = payment.metadata as Record<string, unknown> | null | undefined;
  const createdBy = meta?.createdBy as string | undefined;
  if (createdBy === 'integration:hipay') return 'HiPay Card';
  if (typeof createdBy === 'string' && createdBy.startsWith('integration:')) {
    const provider = createdBy.replace('integration:', '');
    return `${provider.charAt(0).toUpperCase() + provider.slice(1)} Card`;
  }
  return 'Unknown';
}

function getIntegrationPaymentMethodFields(metadata: unknown, t: TFunction<'payments'>): React.ReactNode {
  const meta = metadata as Record<string, unknown> | null | undefined;
  if (!meta || meta.createdBy !== 'integration:hipay') return null;

  const pan = meta.hipayPan as string | undefined;
  const brand = meta.hipayBrand as string | undefined;
  const cardHolder = meta.hipayCardHolder as string | undefined;
  const expMonth = meta.hipayCardExpiryMonth as string | undefined;
  const expYear = meta.hipayCardExpiryYear as string | undefined;
  const last4 = pan ? pan.replace(/\D/g, '').slice(-4) : null;

  if (!brand && !last4 && !cardHolder) return null;

  return (
    <>
      {last4 && (
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.cardNumber')}</p>
          <p className="font-mono">•••• {last4}</p>
        </div>
      )}
      {(expMonth || expYear) && (
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.expiration')}</p>
          <p className="font-mono">
            {expMonth || '—'}/{expYear || '—'}
          </p>
        </div>
      )}
      {brand && (
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm font-medium">{t('cardBrand')}</p>
          <p className="font-medium capitalize">{String(brand)}</p>
        </div>
      )}
      {cardHolder && (
        <div className="space-y-1">
          <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.cardholder')}</p>
          <p className="font-medium">{String(cardHolder)}</p>
        </div>
      )}
    </>
  );
}

interface PaymentDetailsCardProps {
  payment: PaymentDetails;
}

export function PaymentDetailsCard({ payment }: PaymentDetailsCardProps): JSX.Element {
  const { t } = useTranslation('payments');
  const [showEnrichments, setShowEnrichments] = React.useState(false);

  // Check if payment is a test transaction
  const isTestTransaction: boolean = Boolean(
    (payment.transactions && payment.transactions.some((t) => Boolean(t.isTest))) || Boolean(payment.draft),
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('paymentDetails')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Test Transaction Warning */}
        {isTestTransaction && (
          <div className="flex items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-800 dark:bg-yellow-950">
            <div className="flex items-center gap-1 text-yellow-800 dark:text-yellow-200">
              <Shield className="h-4 w-4" />
              <Badge variant="yellow">
                {t('detailsCard.testTransaction')}
              </Badge>
            </div>
            <span className="text-sm text-yellow-700 dark:text-yellow-300">
              {t('detailsCard.testTransactionDescription')}
            </span>
          </div>
        )}

        {/* Basic Payment Info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('status')}</p>
            <PaymentStatusBadge status={payment.status} subStatus={payment.subStatus} />
          </div>

          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.initiatedBy')}</p>
            <InitiatedByBadge initiatedBy={payment.initiatedBy} />
          </div>

          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('amount')}</p>
            <p className="font-semibold">
              {formatMoney(Number(payment.amount || 0), String(payment.currency || 'USD'))}
            </p>
          </div>

          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.chainIndex')}</p>
            <Badge variant="outline" className="font-mono text-xs">
              {Number(payment.chainIndex ?? 0)}
            </Badge>
          </div>

          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.retryIndex')}</p>
            <Badge variant="outline" className="font-mono text-xs">
              {Number(payment.retryIndex ?? 0)}
            </Badge>
          </div>

          <div className="space-y-1">
            <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.created')}</p>
            <p className="text-sm">
              <DateDisplay value={payment.createdAt} />
            </p>
          </div>
        </div>

        {/* Payment Method Details */}
        <div className="space-y-4">
          <h3 className="flex items-center gap-2 font-semibold">
            <CreditCard className="h-4 w-4" />
            {t('paymentMethod')}
          </h3>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <p className="text-muted-foreground text-sm font-medium">{t('type')}</p>
              <div className="flex items-center gap-2">
                <Badge variant="outline">
                  {getPaymentTypeDisplay(payment, t)}
                </Badge>
                {(payment.paymentInstrument?.token || (payment.metadata as any)?.hipayToken) && (
                  <Badge variant="green" className="text-xs">
                    {t('detailsCard.tokenized')}
                  </Badge>
                )}
              </div>
            </div>

            {payment.paymentInstrument?.card ? (
              <>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.cardNumber')}</p>
                  <p className="font-mono">•••• {payment.paymentInstrument.card.last4}</p>
                </div>

                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.expiration')}</p>
                  <p className="font-mono">
                    {payment.paymentInstrument.card.exp_month}/{payment.paymentInstrument.card.exp_year}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-muted-foreground text-sm font-medium">{t('cardBrand')}</p>
                  <p className="font-medium capitalize">
                    {String(payment.paymentInstrument.card.brand || 'Unknown')}
                  </p>
                </div>
              </>
            ) : (
              getIntegrationPaymentMethodFields(payment.metadata, t)
            )}
          </div>

          {/* Security Features — only for payments that were actually processed */}
          {(payment.status === 'succeeded' || payment.status === 'refunded') && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-800 dark:bg-green-950">
              <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
                <Shield className="h-4 w-4" />
                <span className="font-medium">{t('detailsCard.securedBy')}</span>
              </div>
              <p className="mt-1 text-sm text-green-700 dark:text-green-300">{t('detailsCard.securedDescription')}</p>
            </div>
          )}

          {/* Processor Tokens */}
          {payment.paymentInstrument?.processorTokens &&
            payment.paymentInstrument.processorTokens.length > 0 && (
              <div className="space-y-2">
                <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.processorTokens')}</p>
                <div className="space-y-2">
                  {payment.paymentInstrument.processorTokens.map((token) => (
                    <div key={token.id} className="flex items-center gap-2">
                      <Badge variant="outline">{String(token.processor?.name || 'Unknown')}</Badge>
                      <CopyableResourceBadge name="Token" value={token.token} className="font-mono text-xs" />
                    </div>
                  ))}
                </div>
              </div>
            )}

          {/* Card Enrichments */}
          {payment.paymentInstrument?.cardEnrichment && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">{t('detailsCard.enrichments')}</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowEnrichments(!showEnrichments)}
                  className="text-xs"
                >
                  {showEnrichments ? (
                    <>
                      <ChevronUp className="mr-1 h-3 w-3" />
                      {t('detailsCard.hideRawEnrichments')}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="mr-1 h-3 w-3" />
                      {t('detailsCard.showRawEnrichments')}
                    </>
                  )}
                </Button>
              </div>

              {/* BIN Details */}
              {payment.paymentInstrument.cardEnrichment?.data?.enrichments?.binDetails && (
                <div className="space-y-4">
                  <h4 className="font-medium">{t('detailsCard.cardInformation')}</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="space-y-1">
                      <p className="text-muted-foreground font-medium">{t('cardBrand')}</p>
                      <Badge variant="outline" className="font-medium">
                        {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.cardBrand}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <p className="text-muted-foreground font-medium">{t('detailsCard.cardType')}</p>
                      <div className="flex items-center gap-2">
                        <span>
                          {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.type}
                        </span>
                        <span>•</span>
                        <span>
                          {
                            payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails
                              .cardSegmentType
                          }
                        </span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-muted-foreground font-medium">{t('detailsCard.issuingBank')}</p>
                      <p className="font-medium">
                        {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.bank
                          ?.cleanName ||
                          payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.bank?.name}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-muted-foreground font-medium">{t('detailsCard.country')}</p>
                      <p>
                        {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.country?.name}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-muted-foreground font-medium">{t('detailsCard.currency')}</p>
                      <Badge variant="secondary">
                        {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.issuerCurrency}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <p className="text-muted-foreground font-medium">{t('detailsCard.product')}</p>
                      <p className="text-xs">
                        {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.product?.name}
                      </p>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="space-y-2">
                    <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.features')}</p>
                    <div className="flex flex-wrap gap-2">
                      {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.prepaid && (
                        <Badge variant="outline" className="text-xs">
                          {t('detailsCard.prepaid')}
                        </Badge>
                      )}
                      {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.reloadable && (
                        <Badge variant="outline" className="text-xs">
                          {t('detailsCard.reloadable')}
                        </Badge>
                      )}
                      {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails
                        .accountUpdater && (
                        <Badge variant="outline" className="text-xs">
                          {t('detailsCard.accountUpdater')}
                        </Badge>
                      )}
                      {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.domesticOnly && (
                        <Badge variant="outline" className="text-xs">
                          {t('detailsCard.domesticOnly')}
                        </Badge>
                      )}
                      {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails
                        .gamblingBlocked && (
                        <Badge variant="destructive" className="text-xs">
                          {t('detailsCard.gamblingBlocked')}
                        </Badge>
                      )}
                      {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.level2 && (
                        <Badge variant="outline" className="text-xs">
                          {t('detailsCard.level2')}
                        </Badge>
                      )}
                      {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.level3 && (
                        <Badge variant="outline" className="text-xs">
                          {t('detailsCard.level3')}
                        </Badge>
                      )}
                      {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.alm && (
                        <Badge variant="outline" className="text-xs">
                          {t('detailsCard.alm')}
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Authentication Methods */}
                  {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.authentication &&
                    payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.authentication
                      .length > 0 && (
                      <div className="space-y-2">
                        <p className="text-muted-foreground text-sm font-medium">{t('detailsCard.authentication')}</p>
                        <div className="flex flex-wrap gap-2">
                          {payment.paymentInstrument.cardEnrichment.data.enrichments.binDetails.authentication.map(
                            (auth: any, index: number) => (
                              <Badge key={index} variant="blue" className="text-xs">
                                {typeof auth === 'string' ? auth : auth.authenticationName}
                              </Badge>
                            ),
                          )}
                        </div>
                      </div>
                    )}
                </div>
              )}

              {/* Raw Enrichments */}
              {showEnrichments && payment.paymentInstrument.cardEnrichment?.data && (
                <div className="space-y-4">
                  <div className="bg-muted rounded-lg p-4">
                    <h5 className="mb-2 font-medium">{t('detailsCard.cardDetails')}</h5>
                    <pre className="overflow-auto text-xs">
                      {JSON.stringify(
                        payment.paymentInstrument.cardEnrichment.data.enrichments?.cardDetails,
                        null,
                        2,
                      )}
                    </pre>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <h5 className="mb-2 font-medium">{t('detailsCard.binDetails')}</h5>
                    <pre className="overflow-auto text-xs">
                      {JSON.stringify(
                        payment.paymentInstrument.cardEnrichment.data.enrichments?.binDetails,
                        null,
                        2,
                      )}
                    </pre>
                  </div>

                  {payment.paymentInstrument.cardEnrichment.data.data && (
                    <div className="bg-muted rounded-lg p-4">
                      <h5 className="mb-2 font-medium">{t('detailsCard.tokenData')}</h5>
                      <pre className="overflow-auto text-xs">
                        {JSON.stringify(payment.paymentInstrument.cardEnrichment.data.data, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Metadata */}
        {payment.metadata && (
          <>
            <Separator />
            <div className="space-y-3">
              <h3 className="font-semibold">{t('detailsCard.metadata')}</h3>
              <div className="bg-muted rounded-lg p-4">
                <pre className="overflow-auto text-xs">{JSON.stringify(
                  Object.fromEntries(
                    Object.entries(payment.metadata as Record<string, unknown>).filter(
                      ([key]) => !HIDDEN_METADATA_KEYS.has(key)
                    )
                  ),
                  null,
                  2,
                )}</pre>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
